/**
 * Shared /orders hot-path predicates used by subscribe, Redux and loaders.
 * Kept pure so Jest / the stress harness can assert the same code the UI runs.
 */

export type OfferDeadlineSource = {
	offer?: {
		orderOfferTime?: number;
		/** часы сервера в момент отправки снапшота */
		serverTime?: number;
	} | null;
};

const localDeadlines = new WeakMap<object, number>();

/**
 * Дедлайн оффера в часах браузера. `orderOfferTime` считается на сервере,
 * поэтому вместе с ним приходит `serverTime` — часы сервера при отправке.
 * Остаток = orderOfferTime - serverTime, он не зависит от скью часов
 * клиента. Считается один раз на объект оффера (сразу при получении) и
 * кешируется по ссылке.
 */
export const offerDeadlineMs = (
	raw: OfferDeadlineSource | null | undefined,
	now: number = Date.now(),
): number | undefined => {
	const offer = raw?.offer;
	if (!offer || typeof offer.orderOfferTime !== "number") return undefined;
	const cached = localDeadlines.get(offer);
	if (cached !== undefined) return cached;
	const deadline =
		typeof offer.serverTime === "number"
			? now + (offer.orderOfferTime - offer.serverTime)
			: offer.orderOfferTime;
	localDeadlines.set(offer, deadline);
	return deadline;
};

/**
 * Зафиксировать дедлайн оффера в момент получения снапшота (WS-событие
 * или ответ getById). `offerDeadlineMs` считает его по часам сервера
 * относительно «сейчас», поэтому первый вызов должен быть сразу при приёме.
 */
export const primeOfferDeadline = (raw: unknown): void => {
	offerDeadlineMs(raw as OfferDeadlineSource | null | undefined);
};

export const isOfferCacheExpired = (
	raw: OfferDeadlineSource | null | undefined,
	now: number = Date.now(),
): boolean => {
	const deadline = offerDeadlineMs(raw, now);
	return typeof deadline === "number" && now >= deadline;
};

/** setTimeout с задержкой > 2^31-1 мс срабатывает сразу — клампим. */
export const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;

export const clampTimeoutDelay = (delayMs: number): number =>
	Math.min(Math.max(0, delayMs), MAX_TIMEOUT_DELAY_MS);

export const sameOrderItems = <T>(
	prev?: T[] | null,
	next?: T[] | null,
): boolean => {
	if (prev === next) return true;
	if (!prev || !next || prev.length !== next.length) return false;
	for (let i = 0; i < prev.length; i += 1) {
		if (prev[i] !== next[i]) return false;
	}
	return true;
};

export type LocationPoint = {
	id: number;
	date?: number | string;
	point?: { lat?: number; lng?: number };
	executor?: {
		online?: boolean;
		isWorking?: boolean;
		status?: string;
		hasActiveOrder?: boolean;
	};
};

export type SameExecutorLocationGpsOptions = {
	now?: number;
	ttlMs?: number;
	refreshAfterMs?: number;
};

const locationDateMs = (date: LocationPoint["date"]): number => {
	if (typeof date === "number") return date;
	if (typeof date === "string") {
		const parsed = Date.parse(date);
		return Number.isFinite(parsed) ? parsed : NaN;
	}
	return NaN;
};

const isLocationGpsFresh = (
	item: LocationPoint,
	now: number,
	ttlMs: number,
): boolean => {
	if (!item?.point) return false;
	const at = locationDateMs(item.date);
	if (!Number.isFinite(at)) return false;
	return now - at <= ttlMs;
};

export const sameExecutorLocationPoints = (
	prev: LocationPoint[],
	next: LocationPoint[],
	options?: SameExecutorLocationGpsOptions,
): boolean => {
	if (prev === next) return true;
	if (prev.length !== next.length) return false;
	const now = options?.now ?? Date.now();
	const ttlMs = options?.ttlMs && options.ttlMs > 0 ? options.ttlMs : 60_000;
	const refreshAfterMs =
		options?.refreshAfterMs && options.refreshAfterMs > 0
			? options.refreshAfterMs
			: Math.max(10_000, Math.floor(ttlMs / 2));
	for (let i = 0; i < prev.length; i += 1) {
		const left = prev[i];
		const right = next[i];
		if (left !== right) {
			if (left.id !== right.id) return false;
			if (
				left.point?.lat !== right.point?.lat ||
				left.point?.lng !== right.point?.lng
			) {
				return false;
			}
			const prevExecutor = left.executor;
			const nextExecutor = right.executor;
			if (prevExecutor !== nextExecutor) {
				if (
					prevExecutor?.online !== nextExecutor?.online ||
					prevExecutor?.isWorking !== nextExecutor?.isWorking ||
					prevExecutor?.status !== nextExecutor?.status ||
					prevExecutor?.hasActiveOrder !==
						nextExecutor?.hasActiveOrder
				) {
					return false;
				}
			}
			if (left.date !== right.date) {
				const prevFresh = isLocationGpsFresh(left, now, ttlMs);
				const nextFresh = isLocationGpsFresh(right, now, ttlMs);
				// Протух ↔ живой — карте нужен новый date в Redux
				if (prevFresh !== nextFresh) return false;
				if (prevFresh && nextFresh) {
					const prevAt = locationDateMs(left.date);
					// Стоящий шлёт GPS: подновить стор раз в TTL/2, не каждый пакет
					if (
						Number.isFinite(prevAt) &&
						now - prevAt > refreshAfterMs
					) {
						return false;
					}
				}
			}
		}
	}
	return true;
};
